import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, Plus, Trash2, UserCheck } from 'lucide-react';
import { SalesQualificationStepProps } from '@/types/onboarding/salesQualification';
import { createArrayUpdateHelper } from '../utils';

export const StakeholderMapping: React.FC<SalesQualificationStepProps> = ({ data, onUpdate }) => {
  const updateFormData = (field: string, value: unknown) => {
    onUpdate({ ...data, [field]: value });
  };

  const handleArrayUpdate = createArrayUpdateHelper(data, updateFormData);

  const addDecisionMaker = () => {
    const newDecisionMaker = {
      name: '',
      title: '',
      email: '',
      influence: '',
      role: '',
    };
    const currentDecisionMakers = data.decisionMakers || [];
    updateFormData('decisionMakers', [...currentDecisionMakers, newDecisionMaker]);
  };

  const updateDecisionMaker = (index: number, field: string, value: string) => {
    const currentDecisionMakers = [...(data.decisionMakers || [])];
    currentDecisionMakers[index] = { ...currentDecisionMakers[index], [field]: value };
    updateFormData('decisionMakers', currentDecisionMakers);
  };

  const removeDecisionMaker = (index: number) => {
    const currentDecisionMakers = [...(data.decisionMakers || [])];
    currentDecisionMakers.splice(index, 1);
    updateFormData('decisionMakers', currentDecisionMakers);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Stakeholder Mapping</h2>
        <p className="text-gray-600">
          Help us understand your decision-making structure for enterprise implementation
        </p>
      </div>

      {/* Key Decision Makers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Key Decision Makers
          </CardTitle>
          <CardDescription>
            Who are the key people involved in making this enterprise decision?
          </CardDescription>
          <div className="flex justify-end">
            <Button type="button" variant="outline" size="sm" onClick={addDecisionMaker}>
              <Plus className="w-4 h-4 mr-2" />
              Add Decision Maker
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(!data.decisionMakers || data.decisionMakers.length === 0) && (
            <div className="text-center py-8 text-gray-500">
              <UserCheck className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No decision makers added yet. Click "Add Decision Maker" to get started.</p>
            </div>
          )}

          {data.decisionMakers?.map((dm, index) => (
            <Card key={index} className="p-4 border border-gray-200">
              <div className="grid md:grid-cols-5 gap-3">
                <div>
                  <Label htmlFor={`name-${index}`}>Name *</Label>
                  <Input
                    id={`name-${index}`}
                    value={dm.name}
                    onChange={(e) => updateDecisionMaker(index, 'name', e.target.value)}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor={`title-${index}`}>Title *</Label>
                  <Input
                    id={`title-${index}`}
                    value={dm.title}
                    onChange={(e) => updateDecisionMaker(index, 'title', e.target.value)}
                    placeholder="Job title"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor={`email-${index}`}>Email *</Label>
                  <Input
                    id={`email-${index}`}
                    type="email"
                    value={dm.email}
                    onChange={(e) => updateDecisionMaker(index, 'email', e.target.value)}
                    placeholder="email@example.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor={`role-${index}`}>Role *</Label>
                  <Select
                    value={dm.role}
                    onValueChange={(value) => updateDecisionMaker(index, 'role', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="board-president">Board President</SelectItem>
                      <SelectItem value="board-member">Board Member</SelectItem>
                      <SelectItem value="property-manager">Property Manager</SelectItem>
                      <SelectItem value="financial-manager">Financial Manager</SelectItem>
                      <SelectItem value="community-manager">Community Manager</SelectItem>
                      <SelectItem value="resident-representative">
                        Resident Representative
                      </SelectItem>
                      <SelectItem value="vendor-representative">Vendor Representative</SelectItem>
                      <SelectItem value="legal-counsel">Legal Counsel</SelectItem>
                      <SelectItem value="it-manager">IT Manager</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor={`influence-${index}`}>Influence Level *</Label>
                  <Select
                    value={dm.influence}
                    onValueChange={(value) => updateDecisionMaker(index, 'influence', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="primary">Primary Decision Maker</SelectItem>
                      <SelectItem value="influencer">Key Influencer</SelectItem>
                      <SelectItem value="stakeholder">Important Stakeholder</SelectItem>
                      <SelectItem value="user">End User/Implementer</SelectItem>
                      <SelectItem value="advisor">Advisor/Consultant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {data.decisionMakers && data.decisionMakers.length > 1 && (
                <div className="flex justify-end mt-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeDecisionMaker(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </CardContent>
      </Card>

      {/* Decision Process Details */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Decision Timeline</CardTitle>
            <CardDescription>When do you expect to make a decision?</CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={data.decisionTimeline || ''}
              onValueChange={(value) => updateFormData('decisionTimeline', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select timeline" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Ready to decide now</SelectItem>
                <SelectItem value="week">Within 1 week</SelectItem>
                <SelectItem value="month">Within 1 month</SelectItem>
                <SelectItem value="quarter">Within 3 months</SelectItem>
                <SelectItem value="half-year">Within 6 months</SelectItem>
                <SelectItem value="longer">Longer timeline (6+ months)</SelectItem>
                <SelectItem value="depends">Depends on solution fit</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Budget Status</CardTitle>
            <CardDescription>What's the current status of your budget?</CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={data.budgetApproved || ''}
              onValueChange={(value) => updateFormData('budgetApproved', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Budget already approved</SelectItem>
                <SelectItem value="pending">Budget approval pending</SelectItem>
                <SelectItem value="exploring">Still exploring costs</SelectItem>
                <SelectItem value="flexible">Budget flexible based on value</SelectItem>
                <SelectItem value="need-justification">
                  Need ROI justification for approval
                </SelectItem>
                <SelectItem value="board-approval">Requires board approval</SelectItem>
                <SelectItem value="phased">Considering phased budget approach</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Competitive Evaluation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Competitive Solutions Being Evaluated</CardTitle>
          <CardDescription>
            What other solutions are you considering? (Check all that apply)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'Building our own solution in-house',
              'Other community management platforms',
              'Traditional property management software',
              'General business management tools (Salesforce, etc.)',
              'Custom development firms',
              'Staying with current manual processes',
              'Multiple point solutions vs integrated platform',
              'Open source or free alternatives',
              'Legacy system upgrades',
              'Consulting firms for strategy only',
              'Other (please specify in notes)',
              'Not evaluating alternatives - prefer ToolboxLogic',
            ].map((option) => (
              <div key={option} className="flex items-start space-x-2">
                <Checkbox
                  id={`competitive-${option}`}
                  checked={data.competitiveEvaluation?.includes(option) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('competitiveEvaluation', option, !!checked)
                  }
                />
                <Label htmlFor={`competitive-${option}`} className="text-sm leading-relaxed">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Decision Criteria */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Decision Criteria</CardTitle>
          <CardDescription>
            What factors are most important in your decision-making process?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'Total cost of ownership (TCO)',
              'Return on investment (ROI)',
              'Feature completeness and functionality',
              'Ease of implementation and adoption',
              'Integration capabilities',
              'Vendor stability and track record',
              'Security and compliance features',
              'Scalability and future-proofing',
              'Support and training quality',
              'Customization and flexibility',
              'User experience and interface',
              'Time to value and quick wins',
            ].map((criteria) => (
              <div key={criteria} className="flex items-start space-x-2">
                <Checkbox
                  id={`criteria-${criteria}`}
                  checked={data.decisionCriteria?.includes(criteria) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('decisionCriteria', criteria, !!checked)
                  }
                />
                <Label htmlFor={`criteria-${criteria}`} className="text-sm leading-relaxed">
                  {criteria}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <p className="text-sm text-indigo-700">
          <strong>Stakeholder mapping helps us customize our Enterprise approach:</strong>
        </p>
        <ul className="text-sm text-indigo-700 mt-2 space-y-1">
          <li>• Tailor presentations and materials to key decision makers' priorities</li>
          <li>• Structure the evaluation process to fit your decision timeline</li>
          <li>• Provide the right level of technical detail for each stakeholder</li>
          <li>• Plan implementation approach based on your team's capacity and structure</li>
          <li>• Address competitive concerns and differentiate our enterprise value</li>
        </ul>
      </div>
    </div>
  );
};
